import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import GlobalStyles from '@mui/material/GlobalStyles';

import useAppBarScrollTrigger from 'hooks/useAppBarScrollTrigger';
import useDashboardSidebar from 'hooks/useDashboardSidebar';
import useDashboardIntegrations from 'hooks/useDashboardIntegrations';
import useOnboardingProgress from 'hooks/useOnboardingProgress';
import useDashboardSessionNotices from 'hooks/useDashboardSessionNotices';
import useAsyncExpertBotPolling from 'hooks/useAsyncExpertBotPolling';
import useNavPreferencesSync from 'hooks/useNavPreferencesSync';
import Container from 'components/Container';

import { Topbar, Sidebar, Footer, TopNavbar } from './components';
import AlertPopup from 'components/AlertPopup';
import { useDispatch, useSelector } from 'react-redux';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import CircularCountdown from 'components/@2025/CircularCountdown';
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { updateUiState } from 'redux/slices/uiSlice';
import { hideAlert, showAlert } from 'redux/slices/alertSlice';
import { undoSendEmailThunk } from 'redux/slices/emailSlice';
import JoyrideTourGate from '@/features/productTours/JoyrideTourGate';
import GlobalDialogs from './GlobalDialogs';
import { expandedSidebarPx, RAIL_PX } from './sidebarMetrics';

const FULL_WIDTH_HEIGHT_SX = { width: '100%', height: '1px' };
const POINTER_SX = { cursor: 'pointer', '&:hover': { opacity: 0.9 } };
const ROW_CENTER_BETWEEN_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  width: 1,
};
const ROW_CENTER_GAP1_SX = { display: 'flex', alignItems: 'center', gap: 1 };
const ROW_CENTER_GAP2_SX = {
  // padding: '4px 8px',
  display: 'flex',
  gap: 2,
  alignItems: 'center',
};

const Dashboard = ({
  children,
  colorInvert = false,
  bgcolor = 'transparent',
  noTopbar = false,
  noFooter = true,
  leftSidebar = true,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const dashboardSidebarExpanded = useSelector(
    (state) => state.uiStates.dashboardSidebarExpanded,
  );
  const showUndoSendTimer = useSelector(
    (state) => state.uiStates.showUndoSendTimer,
  );
  const showLoginToast = useSelector((state) => state.uiStates.showLoginToast);
  const twoLevelNavPreferred = useSelector(
    (state) => state.uiStates.twoLevelNav,
  );
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const alertState = useSelector((state) => state.alertState);

  const {
    isMd,
    open,
    showBottomNav,
    handleSidebarOpen,
    handleSidebarClose,
  } = useDashboardSidebar();
  const { gmailReadOnly, integrations } = useDashboardIntegrations();
  useOnboardingProgress();
  useDashboardSessionNotices();
  useAsyncExpertBotPolling();
  useNavPreferencesSync();

  const trigger = useAppBarScrollTrigger();

  // The two-level sidebar is a desktop layout: on a phone the sidebar is a
  // temporary drawer, and it stays the one-level nav there.
  const twoLevelNav = twoLevelNavPreferred && !showBottomNav;

  return (
    <ErrorBoundary>
      <JoyrideTourGate />
      <GlobalDialogs
        gmailReadOnly={gmailReadOnly}
        integrations={integrations}
      />
      {/*
        The sidebar's widths as the views read them (see sidebarMetrics.js):
        they depend on the two-level preference, so they are set here rather
        than baked into the views.
      */}
      <GlobalStyles
        styles={{
          ':root': {
            '--dc-sidebar-width': `${expandedSidebarPx(twoLevelNav)}px`,
            '--dc-sidebar-rail-width': `${RAIL_PX}px`,
          },
        }}
      />
      <Box>
        {!noTopbar ? (
          !showBottomNav ? (
            <AppBar
              position={'sticky'}
              sx={{
                top: 0,
                // The same surface as the sidebar's icon rail (the theme's
                // page `background`), so the topbar and the rail read as one
                // frame around the nav panel and the page. A hairline rule at
                // all times; the scrolled state used to swap it for a drop
                // shadow the spec rules out.
                backgroundColor: theme.palette.custom.background,
                // borderBottom: `1px solid ${theme.palette.custom.border}`,
                boxShadow: 'none',
                // Two-level: the bar starts at the rail's right edge and runs
                // over the nav panel, which sits behind it (see `Sidebar`).
                ...(twoLevelNav
                  ? {
                      marginLeft: `${RAIL_PX}px`,
                      width: `calc(100% - ${RAIL_PX}px)`,
                    }
                  : {}),
                // `bgcolor` stays honoured for any caller that opts out of the
                // card surface.
                ...(bgcolor !== 'transparent'
                  ? { backgroundColor: bgcolor }
                  : {}),
                borderRadius: 0,
                // border: 'none',

                borderBottom: `1px solid ${theme.palette.custom.border}`,
              }}
              elevation={0}
            >
              <Container
                paddingY={0}
                paddingX={isMd ? 4 : 1}
                maxWidth={{ sm: 1, md: 1 }}
              >
                <Topbar
                  onSidebarOpen={handleSidebarOpen}
                  colorInvert={trigger ? false : colorInvert}
                  twoLevel={twoLevelNav}
                />
              </Container>
            </AppBar>
          ) : (
            <TopNavbar
              onSidebarOpen={handleSidebarOpen}
              onClose={() => {}}
              open={true}
              variant="permanent"
              expanded={dashboardSidebarExpanded}
            />
          )
        ) : null}
        {/* <Divider orientation="horizontal" sx={FULL_WIDTH_HEIGHT_SX} /> */}
        {(leftSidebar && !showBottomNav) || open ? (
          <Sidebar
            onClose={handleSidebarClose}
            open={open}
            variant={open ? 'temporary' : 'permanent'}
            expanded={dashboardSidebarExpanded}
            twoLevel={twoLevelNav}
            underTopbar={!noTopbar}
          />
        ) : null}
        <main>
          <AlertPopup
            open={alertState.open}
            autoHideDuration={6000}
            handleClose={() => dispatch(hideAlert())}
            message={alertState.message}
            type={alertState.type}
            verticalLocation={alertState.vertical}
            horizontalLocation={alertState.horizontal}
            onClick={alertState.onClick}
          ></AlertPopup>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={showLoginToast}
            autoHideDuration={5000}
            onClose={(_, reason) => {
              if (reason !== 'clickaway') {
                dispatch(
                  updateUiState({ key: 'showLoginToast', value: false }),
                );
              }
            }}
          >
            <Alert
              elevation={6}
              variant="filled"
              severity="info"
              action={
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(
                      updateUiState({ key: 'showLoginToast', value: false }),
                    );
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
              sx={POINTER_SX}
              onClick={() => {
                history.push('/action-plan');
              }}
            >
              Click here to view your actions and meetings for today.
            </Alert>
          </Snackbar>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            open={showUndoSendTimer}
            onClose={() => {}}
            color="primary"
          >
            <SnackbarContent
              sx={{
                background: 'var(--dc-card)',
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 'var(--dc-radius-pill)',
                '& .MuiPaper-root-MuiSnackbarContent-root': {
                  padding: '4px',
                  width: '120px',
                },
              }}
              message={
                <Box sx={ROW_CENTER_BETWEEN_SX}>
                  <Typography color={'text.secondary'}>
                    Sending Email...
                  </Typography>
                </Box>
              }
              action={
                <React.Fragment>
                  <Box sx={ROW_CENTER_GAP1_SX}>
                    <Box sx={ROW_CENTER_GAP1_SX}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => {
                          dispatch(
                            undoSendEmailThunk(showUndoSendTimer?.undoAsyncId),
                          )
                            .unwrap()
                            .then(() => {
                              dispatch(
                                updateUiState({
                                  key: 'replyModeThread',
                                  value: {
                                    threadId: showUndoSendTimer?.thread?.id,
                                    conversationId:
                                      showUndoSendTimer?.thread
                                        ?.interactionType === 'email-inbound' ||
                                      showUndoSendTimer?.thread
                                        ?.interactionType ===
                                        'email-inbound-internal'
                                        ? showUndoSendTimer?.thread
                                            ?.interactionInboundEmailResponseConversationId
                                        : showUndoSendTimer?.thread
                                            ?.interactionOutboundEmailResponseConversationId,
                                    replyAll: true,
                                    emailBody: showUndoSendTimer?.emailBody,
                                  },
                                }),
                              );
                              setTimeout(() => {
                                dispatch(
                                  updateUiState({
                                    key: 'showUndoSendTimer',
                                    value: null,
                                  }),
                                );
                                dispatch(
                                  updateUiState({
                                    key: 'asyncThreadProcessing',
                                    value: null,
                                  }),
                                );
                              }, 500);
                            })
                            .catch(() => {
                              dispatch(
                                showAlert({
                                  message:
                                    'Failed to undo sent email. Please try again!',
                                  showCross: true,
                                  title: null,
                                  type: 'error',
                                  autoHideDuration: 2000,
                                  vertical: 'top',
                                  horizontal: 'center',
                                }),
                              );
                            });
                        }}
                        sx={ROW_CENTER_GAP2_SX}
                      >
                        UNDO{' '}
                        <CircularCountdown
                          n={userDetails?.userSetting?.undoTimeInSec || 5}
                        />
                      </Button>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="close"
                      color="inherit"
                      onClick={() => {
                        dispatch(
                          updateUiState({
                            key: 'showUndoSendTimer',
                            value: null,
                          }),
                        );
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </React.Fragment>
              }
            />
          </Snackbar>
          <Box paddingTop={showBottomNav ? 6 : 0}>{children}</Box>
          {!noFooter ? <Divider /> : null}
        </main>
        {!noFooter ? (
          <Container paddingY={1} maxWidth={{ sm: 1, md: 1236 }}>
            <Footer />
          </Container>
        ) : null}
      </Box>
    </ErrorBoundary>
  );
};

Dashboard.propTypes = {
  children: PropTypes.node,
  colorInvert: PropTypes.bool,
  bgcolor: PropTypes.string,
  noTopbar: PropTypes.bool,
  noFooter: PropTypes.bool,
  leftSidebar: PropTypes.bool,
};

export default Dashboard;
